/**
 * @fileoverview Student Activity page - shows student's projects
 */
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import {
  Alert,
  Anchor,
  Badge,
  Flex,
  Pagination,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from 'components'
import { Table } from '@mantine/core'
import { IconAlertCircle, IconArrowLeft, IconFolder } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'components/Layout'
import { EmptyStateBox } from 'components/EmptyStateBox'
import { CurrentUserContextProvider } from 'context'
import { useStudentActivity } from './useStudentActivity'

/** Format date for tooltip */
function formatDateTime(dateStr: string): string {
  if (!dateStr) return ''
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

/** Safe student name extraction from location state */
function getStudentName(state: unknown): string {
  if (!state || typeof state !== 'object') return ''
  const s = state as { student?: { firstName?: string; lastName?: string } }
  if (!s.student) return ''
  const first = s.student.firstName ?? ''
  const last = s.student.lastName ?? ''
  return `${first} ${last}`.trim()
}

function StudentActivityContent() {
  const { studentId } = useParams<{ studentId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const studentName = getStudentName(location.state)
  const { tab, page, projects, pagination, counts, loading, error, setTab, setPage } =
    useStudentActivity(studentId)

  // Error state if no studentId
  if (!studentId) {
    return (
      <Layout>
        <Stack spacing="md" p="md">
          <Alert icon={<IconAlertCircle />} color="red" title={t('common.error')}>
            {t('student_activity.invalid_student')}
          </Alert>
          <Anchor component="button" onClick={() => navigate('/teacher/manage-students')}>
            {t('student_activity.back')}
          </Anchor>
        </Stack>
      </Layout>
    )
  }

  return (
    <Layout>
      <Stack spacing="lg">
        {/* Header */}
        <div>
          <Anchor
            component="button"
            size="sm"
            onClick={() => navigate('/teacher/manage-students')}
            sx={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <IconArrowLeft size={14} />
            {t('student_activity.back')}
          </Anchor>
          <Title order={2} mt="xs">
            {t('student_activity.title')}
            {studentName ? ` — ${studentName}` : ''}
          </Title>
        </div>

        {/* Tabs */}
        <Tabs
          value={tab}
          onTabChange={v => v && setTab(v as 'assigned' | 'completed')}
          styles={theme => ({
            tab: {
              '&[data-active]': {
                borderColor: theme.colors.teal[6],
              },
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab
              value="assigned"
              rightSection={
                <Badge size="sm" color="teal" variant="filled">
                  {counts.assigned}
                </Badge>
              }
            >
              {t('student_activity.tab_assigned')}
            </Tabs.Tab>
            <Tabs.Tab
              value="completed"
              rightSection={
                <Badge size="sm" color="teal" variant="filled">
                  {counts.completed}
                </Badge>
              }
            >
              {t('student_activity.tab_completed')}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Error State */}
        {error && (
          <Alert icon={<IconAlertCircle />} color="orange">
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Table horizontalSpacing="md" verticalSpacing="sm">
            <thead>
              <tr>
                <th><Text c="dimmed" fw={500}>{t('student_activity.col_name')}</Text></th>
                <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_class')}</Text></th>
                <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_grade')}</Text></th>
                <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_created_by')}</Text></th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map(i => (
                <tr key={i}>
                  <td><Skeleton h={20} /></td>
                  <td><Skeleton h={20} w={100} /></td>
                  <td><Skeleton h={20} w={100} /></td>
                  <td><Skeleton h={20} w={100} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Empty State */}
        {!loading && !error && projects.length === 0 && (
          <EmptyStateBox
            withBorder
            icon={<IconFolder size={48} stroke={1.5} color="gray" />}
            text={t(`student_activity.empty_${tab}`)}
          />
        )}

        {/* Data State */}
        {!loading && !error && projects.length > 0 && (
          <>
            <Table horizontalSpacing="md" verticalSpacing="sm">
              <thead>
                <tr>
                  <th><Text c="dimmed" fw={500}>{t('student_activity.col_name')}</Text></th>
                  <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_class')}</Text></th>
                  <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_grade')}</Text></th>
                  <th style={{ width: 150 }}><Text c="dimmed" fw={500}>{t('student_activity.col_created_by')}</Text></th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id}>
                    <td>
                      <Anchor component={Link} to={`/projects/${p.id}`} c="dark" fw={400}>
                        {p.name}
                      </Anchor>
                    </td>
                    <td>
                      <Text c={p.className === '—' ? 'dimmed' : undefined}>{p.className}</Text>
                    </td>
                    <td>
                      <Text c={p.gradeName === '—' ? 'dimmed' : undefined}>{p.gradeName}</Text>
                    </td>
                    <td>
                      <Tooltip label={formatDateTime(p.updatedAt)} disabled={!p.updatedAt} position="top">
                        <Text component="span">{p.createdBy}</Text>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pagination.pages > 1 && (
              <Flex justify="center" mt="md">
                <Pagination value={page} onChange={setPage} total={pagination.pages} />
              </Flex>
            )}
          </>
        )}
      </Stack>
    </Layout>
  )
}

export function StudentActivityPage() {
  return (
    <CurrentUserContextProvider>
      <StudentActivityContent />
    </CurrentUserContextProvider>
  )
}
